<script lang="ts" setup>
defineProps<{
  title: string | undefined
}>()

function setActiveTab(option: string) {
  tab.value = option
  emit('update-tab', option)
}

const emit = defineEmits<{
  (event: 'update-tab', tab: string): void
}>()

import { ref, onMounted, onBeforeUnmount } from 'vue'

const tab = ref('Startup')
const tabs = ['Established Company', 'Startup']

let interval: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  interval = setInterval(() => {
    const currentIndex = tabs.indexOf(tab.value)
    const nextIndex = (currentIndex + 1) % tabs.length
    tab.value = tabs[nextIndex]
    emit('update-tab', tab.value)
  }, 5000)
})

onBeforeUnmount(() => {
  if (interval) {
    clearInterval(interval)
  }
})
</script>

<template>
  <div class="min-h-[30rem] rotate-[-4deg]">
    <div
      class="relative flex w-[445px] flex-col justify-center rounded-lg border-2 border-gray-800 bg-white px-8 pb-4 pt-10 shadow-lg"
    >
      <div
        class="absolute inset-0 -z-10 before:absolute before:inset-0 before:z-[-1] before:rotate-6 before:rounded-lg before:border before:bg-gray-50 after:absolute after:inset-0 after:z-[-2] after:rotate-12 after:rounded-lg after:border after:bg-gray-100"
      ></div>

      <h3 class="mb-4 bg-gray-900 bg-clip-text px-4 text-center text-3xl font-bold text-transparent">
        {{ title }}
      </h3>

      <div class="flex w-full items-center justify-between rounded-lg border-2 border-gray-200 bg-gray-100 p-1 px-4">
        <button
          v-for="option in tabs"
          :key="option"
          @click="setActiveTab(option)"
          :class="[
            'text-md relative z-50 flex items-center rounded-lg px-6 py-2 font-semibold transition before:mr-2 before:block before:h-8 before:rounded-lg',
            tab === option ? 'animate-grow-before border border-gray-200 bg-gray-50 text-white' : 'text-gray-800',
          ]"
        >
          {{ option }}
        </button>
      </div>

      <div class="mb-4 mt-4">
        <transition name="fade" mode="out-in">
          <div :key="tab" class="tab-content">
            <slot />
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
@keyframes growWidth {
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
}

.animate-grow-before::before {
  content: '';
  position: absolute;
  inset: 0;
  height: 100%;
  width: 0;
  z-index: -10;
  transition: width 4s cubic-bezier(0.4, 0, 0.2, 1); /* Start fast, end slow */
  animation: growWidth 4s cubic-bezier(0.4, 0, 0.2, 1) forwards; /* 3 seconds duration */
  @apply bg-main-500 border-main-200;
}

.bg-clip-text {
  background-clip: text;
}

.tab-content {
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-enter-to {
  opacity: 1;
  transform: translateY(0);
}
</style>
